import React, { useEffect, useState } from 'react';
import { arztService } from '../../services/arzt.service';
import { Arzt, AppType } from '../../types';
import WeeklyScheduleReadOnly from '../zuweisung/WeeklyScheduleReadOnly';
import { useEnvironment } from '../../context/EnvironmentContext';

interface ArztListProps {
  onEdit: (arzt: Arzt) => void;
  onDelete: (arzt: Arzt) => void;
  onCreate: () => void;
  refreshTrigger?: number;
}

const ArztList: React.FC<ArztListProps> = ({ onEdit, onDelete, onCreate, refreshTrigger }) => {
  const { currentApp } = useEnvironment();
  const [aerzte, setAerzte] = useState<Arzt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArztForSchedule, setSelectedArztForSchedule] = useState<Arzt | null>(null);

  const fetchAerzte = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await arztService.getAllAerzte(currentApp as AppType);
      setAerzte(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Ärzte');
    } finally {
      setLoading(false);
    }
  }, [currentApp]);

  useEffect(() => {
    fetchAerzte();
  }, [refreshTrigger, fetchAerzte]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (selectedArztForSchedule) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Zuweisungen für {selectedArztForSchedule.vorname} {selectedArztForSchedule.nachname}
          </h2>
          <button
            onClick={() => setSelectedArztForSchedule(null)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            ← Zurück zur Liste
          </button>
        </div>
        <WeeklyScheduleReadOnly arztId={selectedArztForSchedule.id} refreshTrigger={refreshTrigger} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Ärzte Liste</h2>
        <button
          onClick={onCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          Neuer Arzt
        </button>
      </div>

      {aerzte.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Ärzte vorhanden. Erstellen Sie einen neuen Arzt.
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vorname
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nachname
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualifikation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefon
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {aerzte.map((arzt) => (
                <tr key={arzt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {arzt.vorname}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {arzt.nachname}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {arzt.qualifikation || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {arzt.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {arzt.telefon || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setSelectedArztForSchedule(arzt)}
                      className="text-green-600 hover:text-green-900 mr-4"
                    >
                      📅 Zuweisungen
                    </button>
                    <button
                      onClick={() => onEdit(arzt)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => onDelete(arzt)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ArztList;
